import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Pause, Zap, GitBranch, Monitor, Brain, Check, Database, RefreshCcw } from "lucide-react";

const workflowSteps = [
  { id: 0, name: "Goal", desc: "Define objectives & constraints", icon: Zap, context: ["User Input", "Safety Policies"] },
  { id: 1, name: "Planner", desc: "Generate plan + subgoals", icon: GitBranch, context: ["Goals", "Historical Runs"] },
  { id: 2, name: "Executor", desc: "Run actions / call tools", icon: Check, context: ["Execution Plan", "APIs & Toolsets"] },
  { id: 3, name: "Monitor", desc: "Observe outcomes & metrics", icon: Monitor, context: ["Metrics", "Execution Traces"] },
  { id: 4, name: "Learner", desc: "Update model / feedback", icon: Brain, context: ["Trajectory Feedback", "Memory Cache"] },
];

function AgenticWorkflow() {
  const [playing, setPlaying] = useState(true);
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!playing) return;
    const interval = setInterval(() => {
      setStep((s) => (s + 1) % workflowSteps.length);
    }, 2500);
    return () => clearInterval(interval);
  }, [playing]);

  const togglePlaying = () => {
    setPlaying(!playing);
  };

  const handleStepClick = (idx) => {
    setPlaying(false);
    setStep(idx);
  };

  const currentStepData = workflowSteps[step];

  return (
    <section id="agentic-workflow-section" className="section-padding bg-dark">
      <div className="container">
        <div className="workflow-container">
          
          <header className="workflow-header">
            <div>
              <h2 className="text-white">Agentic Workflow with Context Feedback Loop</h2>
              <p className="text-slate-300">
                Visualizing how context flows forward through planner stages and loops back autonomously.
              </p>
            </div>
            <div className="workflow-controls">
              <button className="workflow-button" onClick={togglePlaying}>
                {playing ? <Pause size={16} /> : <Play size={16} />}
                <span>{playing ? "Pause" : "Play"}</span>
              </button>
              <div className="workflow-step-counter">
                Step {step + 1}/{workflowSteps.length}
              </div>
            </div>
          </header>

          <main className="workflow-main">
            <div className="workflow-steps-container">
              {workflowSteps.map((s, idx) => {
                const active = idx === step;
                const Icon = s.icon;

                return (
                  <div
                    key={s.id}
                    className={`workflow-step ${active ? "active" : ""}`}
                    onClick={() => handleStepClick(idx)}
                  >
                    <motion.div
                      animate={{
                        y: active ? -8 : 0,
                        borderColor: active ? "var(--primary)" : "var(--glass-border)",
                        backgroundColor: active ? "rgba(6, 182, 212, 0.08)" : "rgba(255, 255, 255, 0.02)",
                        boxShadow: active ? "0 12px 35px var(--primary-glow)" : "0 10px 30px rgba(0, 0, 0, 0.3)"
                      }}
                      transition={{ type: "spring", stiffness: 250, damping: 20 }}
                      className="workflow-step-card"
                    >
                      <div className="flex items-center justify-center mb-3">
                        <Icon className={`workflow-step-icon ${active ? "text-primary" : ""}`} />
                      </div>
                      <h3 className="workflow-step-name text-white">{s.name}</h3>
                      <p className="workflow-step-desc">{s.desc}</p>
                      
                      <div className="workflow-context-container">
                        {s.context.map((ctx, i) => (
                          <motion.span
                            key={i}
                            animate={{ opacity: active ? 1 : 0.4 }}
                            className="workflow-context-tag"
                          >
                            {ctx}
                          </motion.span>
                        ))}
                      </div>
                    </motion.div>

                    {/* Responsive forward context connectors */}
                    {idx < workflowSteps.length - 1 && (
                      <div className="workflow-connector">
                        <div
                          className="workflow-connector-progress"
                          style={{ width: idx < step ? "100%" : idx === step ? "50%" : "0%" }}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Pulsing feedback loop line (Learner → Goal) */}
            <div className={`feedback-loop ${step === 4 ? "visible" : ""}`}>
              <svg viewBox="0 0 400 100" preserveAspectRatio="none" className="feedback-loop-svg">
                <path d="M380 50 C300 10, 100 10, 20 50" />
              </svg>
              <div className="feedback-loop-text">
                <RefreshCcw className="w-4 h-4" />
                <span>Continuous Learning: Trajectory Loops Back to Optimize Goal</span>
              </div>
            </div>

            {/* Active context and memory trace overlay */}
            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
                className="workflow-description-box visible"
              >
                <div className="workflow-description-content">
                  <Database className="workflow-description-icon" />
                  <div className="workflow-description-text">
                    <h4>{currentStepData.name} State telemetry</h4>
                    <p>
                      Active Agent Memory: <strong>[{currentStepData.context.join(" | ")}]</strong>. Executing
                      reasoning chain and validating trajectory limits.
                    </p>
                  </div>
                  <div className="workflow-description-stage">Stage {step + 1}</div>
                </div>
              </motion.div>
            </AnimatePresence>
          </main>

          <footer className="workflow-footer">
            <p>
              Telemetry logs indicate autonomous context flow moving sequentially, with the Learner updating the base
              LLM model weights and policy memory dynamically.
            </p>
          </footer>

        </div>
      </div>
    </section>
  );
}

export default AgenticWorkflow;